import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { i18nMessages } from '../../Logic/CodeAccessible.i18n.Messages'
import { CapeeshColors } from '../../assets/ColorPalette'
import {
    CourseStatResultSortColumn,
    formatNormalizedAsPercent,
} from '../../Views/Private/CourseStatisticsConstants'

const useStyles = makeStyles(() => ({
    headerCell: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        whiteSpace: 'nowrap',
    },
    primaryCell: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '14px',
    },
    secondaryCell: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
    },
}))

const COLUMNS = [
    { key: CourseStatResultSortColumn.User, messageKey: 'courseStatisticsResultColumnUser', numeric: false },
    { key: CourseStatResultSortColumn.Classroom, messageKey: 'courseStatisticsResultColumnClassroom', numeric: false },
    { key: CourseStatResultSortColumn.Topic, messageKey: 'courseStatisticsColumnTopic', numeric: false },
    { key: CourseStatResultSortColumn.AverageScore, messageKey: 'courseStatisticsAverageScore', numeric: true },
    { key: CourseStatResultSortColumn.Progress, messageKey: 'courseStatisticsResultColumnProgress', numeric: true },
    {
        key: CourseStatResultSortColumn.LastActivity,
        messageKey: 'courseStatisticsResultColumnLastActivity',
        numeric: true,
    },
]

/**
 * The matched users. The only place in the feature where individuals are named, and always the
 * result of an explicit query.
 *
 * Sorting is controlled by the parent, because the same order has to be sent with an Excel export
 * so the downloaded file matches the screen. The comparator lives in CourseStatisticsConstants
 * alongside a note about its backend twin.
 */
const CourseStatsUserResultTable = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const entries = props.entries || []
    if (entries.length === 0) {
        return null
    }

    const displayName = (entry) => entry.name || entry.dashboardNickName || entry.userId

    const isSorted = (column) => props.sortBy === column
    const direction = props.sortDescending ? 'desc' : 'asc'

    return (
        <TableContainer style={{ marginTop: '16px' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {COLUMNS.map((column) => (
                            <TableCell
                                key={column.key}
                                align={column.numeric ? 'right' : 'left'}
                                sortDirection={isSorted(column.key) ? direction : false}>
                                <TableSortLabel
                                    active={isSorted(column.key)}
                                    direction={isSorted(column.key) ? direction : 'asc'}
                                    onClick={() => props.onSort(column.key)}>
                                    <Typography className={classes.headerCell}>
                                        {intl.formatMessage(i18nMessages[column.messageKey])}
                                    </Typography>
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry, index) => (
                        <TableRow key={`${entry.userId}_${entry.topicId || 'all'}_${index}`}>
                            <TableCell>
                                <Typography className={classes.primaryCell}>{displayName(entry)}</Typography>
                                {entry.email && (
                                    <Typography className={classes.secondaryCell}>{entry.email}</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Typography className={classes.secondaryCell}>
                                    {(entry.classrooms || []).join(', ')}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography className={classes.secondaryCell}>
                                    {entry.topicName || entry.courseGroupName || '-'}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                {formatNormalizedAsPercent(entry.averageScoreNormalized)}
                            </TableCell>
                            <TableCell align="right">
                                {entry.lessonsPassed}/{entry.lessonsInCourse}
                            </TableCell>
                            <TableCell align="right">
                                <Typography className={classes.secondaryCell}>
                                    {entry.lastActivityAt ? moment(entry.lastActivityAt).format('ll') : '-'}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default CourseStatsUserResultTable
